import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonSearchBar from '../../Components/CommonSearchBar';
import CommonDropdown from '../../Components/CommonDropdown';
import CommonTable from '../../Components/CommonTable';
import CommonButton from '../../Components/CommonButton';
import CommonPagination from '../../Components/CommonPagination';
import StatusBadge from '../../Components/StatusBadge';
import ButtonLoader from '../../Components/ButtonLoader';
import { getTprmAssessmentsService, get_questionnaires } from '../../services';
import { formatDate, MISSING_TEXT, safeArray } from '../tpcrmUtils';
import '../../styles/TpcrmPortal.scss';

const PER_PAGE = 10;

// Ordered along the actual lifecycle rather than alphabetically, so the list
// reads as the path an assessment takes.
//
// `under_review`, `approved` and `request_resubmission` were missing until now.
// They are exactly the three states VendorAssessmentView's status picker writes
// (STATUS_PICKER_OPTIONS), so an assessment a reviewer had moved to Under
// Review vanished from every filter except "All Statuses" — the one query a
// reviewer most wants ("what is on my desk") was the one they could not run.
const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'sent', label: 'Sent' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'request_resubmission', label: 'Request Resubmission' },
    { value: 'completed', label: 'Completed' },
    { value: 'expired', label: 'Expired' },
];

// Same mapping VendorAssessmentView.js uses for the assessment-level status
// pill — kept in sync manually since the two files don't share a utils module.
// Intentionally no `|| 'neutral'` fallback at the call site — that forced
// approved/under_review to grey instead of letting StatusBadge auto-detect
// them as green/amber from its own built-in status table.
const STATUS_VARIANT = {
    sent: 'info', in_progress: 'warning', submitted: 'info',
    completed: 'success', expired: 'neutral', rejected: 'danger',
    // `request_resubmission` is the only new status StatusBadge's own table
    // doesn't cover, so it would fall back to grey. 'purple' matches the chip
    // VendorAssessmentView's status picker uses for it. `under_review` and
    // `approved` stay absent on purpose — StatusBadge already maps those to
    // amber/green itself (see the note above).
    request_resubmission: 'purple',
};

// Cross-vendor "browse every assessment" list — the landing page the
// Assessments sidebar item needs. This used to complement a similar
// "Assignments" tab on the Questionnaires page, but that tab was a strict
// subset of what this page already does (same getTprmAssessmentsService
// data, scoped to one questionnaire, no vendor/status filtering) — removed
// as redundant. This is now the only place to browse/filter assessments.
function TpcrmAssessmentsView() {
    const navigate = useNavigate();
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [questionnaireFilter, setQuestionnaireFilter] = useState('');
    const [questionnaireOptions, setQuestionnaireOptions] = useState([{ value: '', label: 'All Questionnaires' }]);
    const [currentPage, setCurrentPage] = useState(0);

    useEffect(() => {
        const fetchAssessments = async () => {
            setLoading(true);
            try {
                const response = await getTprmAssessmentsService('');
                const data = response?.statusCode?.response_data?.data?.assessments || response?.data?.assessments || [];
                setAssessments(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Failed to load assessments:', error);
                setAssessments([]);
            } finally {
                setLoading(false);
            }
        };
        fetchAssessments();
    }, []);

    useEffect(() => {
        const loadQuestionnaires = async () => {
            try {
                const response = await get_questionnaires();
                const data = response?.statusCode?.response_data?.data?.questionnaires || [];
                setQuestionnaireOptions([
                    { value: '', label: 'All Questionnaires' },
                    ...data.map((q) => ({ value: q.name, label: q.display_name || q.name })),
                ]);
            } catch (error) {
                console.error('Failed to load questionnaires:', error);
            }
        };
        loadQuestionnaires();
    }, []);

    const filteredAssessments = useMemo(() => {
        const term = search.trim().toLowerCase();
        return safeArray(assessments).filter((item) => {
            const matchesSearch = !term || (item?.vendor_name || '').toLowerCase().includes(term);
            const matchesStatus = !statusFilter || item?.status === statusFilter;
            const matchesQuestionnaire = !questionnaireFilter || item?.questionnaire_name === questionnaireFilter;
            return matchesSearch && matchesStatus && matchesQuestionnaire;
        });
    }, [assessments, search, statusFilter, questionnaireFilter]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search, statusFilter, questionnaireFilter]);

    const pageCount = Math.max(1, Math.ceil(filteredAssessments.length / PER_PAGE));
    const pagedAssessments = filteredAssessments.slice(currentPage * PER_PAGE, currentPage * PER_PAGE + PER_PAGE);
    const handlePageClick = ({ selected }) => setCurrentPage(selected);

    const headers = ['Vendor', 'Questionnaire', 'Status', 'Sent', 'Expires', 'Responses', 'Action'];
    const rows = pagedAssessments.map((item) => ({
        data: [
            item?.vendor_name
                ? item.vendor_name
                : <span className="tpcrm-cell-empty">{MISSING_TEXT.vendor}</span>,
            item?.questionnaire_name || 'Default',
            (item?.status_display || item?.status)
                ? <StatusBadge dot={false} variant={STATUS_VARIANT[item?.status]} status={item?.status_display || item?.status} />
                : <span className="tpcrm-cell-empty">{MISSING_TEXT.status}</span>,
            formatDate(item?.sent_at || item?.created_at),
            formatDate(item?.expires_at),
            `${item?.completed_responses ?? 0}/${item?.total_questions ?? 0}`,
            <CommonButton
                text="View"
                className="tpcrm-row-btn"
                disable={!item?.id}
                onClick={() => navigate(`/vendor/assessment/${item.id}/responses`)}
            />,
        ],
    }));

    return (
        <div className="tpcrm-page">
            <div className="tpcrm-card cmn_elevated_card">
                <div className="tpcrm-card-header">
                    <div>
                        <h3>Assessments</h3>
                        <div className="tpcrm-card-subtitle">
                            {loading ? 'Loading assessments' : `${filteredAssessments.length} assessments`}
                        </div>
                    </div>
                </div>

                <div className="tpcrm-page-actions tpcrm-filters-row">
                    <CommonSearchBar
                        placeholder="Search by vendor"
                        value={search}
                        onChange={setSearch}
                        minWidth={200}
                        style={{ width: 220, maxWidth: 220, flex: '0 0 auto' }}
                    />
                    <CommonDropdown ariaLabel="Status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} minWidth={150} />
                    <CommonDropdown ariaLabel="Questionnaire" value={questionnaireFilter} onChange={setQuestionnaireFilter} options={questionnaireOptions} minWidth={180} />
                </div>

                {loading ? (
                    <div className="tpcrm-loader"><ButtonLoader /></div>
                ) : (
                    <>
                        <CommonTable headers={headers} rows={rows} noPadding noScroll />
                        {pageCount > 1 && (
                            <CommonPagination handlePageClick={handlePageClick} pageCount={pageCount} forcePage={currentPage} />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default TpcrmAssessmentsView;
